import React, { useCallback } from 'react';
import { ButtonsGroup, PaperStyled } from '../../index.styles';
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore';
import Button from 'components/control/button/button';
import { EButtonType } from 'components/control/button/button.types';
import { Wrapper, Header, FieldsWrapper, Field } from './index.styles';
import RejectDialog from './dialogs/reject';
import ApproveDialog from './dialogs/approve';
import { toast } from 'react-toastify';
import { useRouter } from 'hooks/use-router';
import { ERoutesType, ERoutesPrivate, routes } from 'types/routes';
import { useSelector } from 'react-redux';
import { selectorPermissions } from 'redux/selectors';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';
import SyncIcon from '@material-ui/icons/Sync';
import Skeleton from '@material-ui/lab/Skeleton';
import {
    EEkycStatuesesApi,
    IEKYCDetails,
    IPEPCFTCheck,
    TCFTResult,
    TUserEkycSetResponse,
} from '../../../../../../types/entities/users';
import useRevalidateCftPost from './hooks/use-revalidate-cft-post';
import useUsersEkycSet from '../../../users/hooks/use-user-ekyc-set';
import PassedIcon from '../../../../../../components/icons/PassedIcon';
import messages from 'messages';
import { EPendingTaskTypeName } from 'types/entities/pending-tasks';

interface ApprovalProps {
    setPrevPage: () => void;
    data: IPEPCFTCheck;
    updatedData: IEKYCDetails;
    refetch: () => void;
    userId: string;
    disableProcessing: boolean;
}

const CftPepAndApprovalComponents = ({
    setPrevPage,
    data,
    userId,
    disableProcessing,
    refetch,
    updatedData,
}: ApprovalProps) => {
    const { history } = useRouter();
    const permissions = useSelector(selectorPermissions);
    const [rejectDialogOpen, setRejectDialogOpen] = React.useState(false);
    const [approveDialogOpen, setApproveDialogOpen] = React.useState(false);

    const handleOnSuccessMutation = useCallback(() => {
        refetch();
    }, [refetch]);

    const redirectToUserList = () => {
        history.push(`${routes[ERoutesType.Private][ERoutesPrivate.Users]}`);
    };

    const [usersEkycSet] = useUsersEkycSet({
        onSuccess: (resp) => {
            const pendingTaskType =
                (resp as TUserEkycSetResponse).statusId ===
                EEkycStatuesesApi.EKYC_STATUS_REJECTED
                    ? EPendingTaskTypeName.REJECTED_EKYC
                    : EPendingTaskTypeName.APPROVED_EKYC;
            toast.success(
                messages.pendingTasks.onCreatePendingTask.onSuccess(
                    pendingTaskType,
                ),
                {
                    pauseOnHover: true,
                },
            );
            redirectToUserList();
        },
        onError: (e) => {
            toast.error(`Error: ${e}`, {
                pauseOnHover: true,
            });
        },
    });

    const [onRevalidateCft, { isLoading: isValidating }] = useRevalidateCftPost(
        {
            onSuccess: () => {
                handleOnSuccessMutation();
                toast.success('Requested to revalidate CFT successfully!');
            },
            onError: (e) => {
                toast.error(`Error: ${e}`, {
                    pauseOnHover: true,
                });
            },
        },
    );

    const cftInfo = (data?.results as TCFTResult[]) || [];

    return (
        <Wrapper>
            <Header>
                <h4>
                    {
                        'Combating the Financing of Terrorism and Politically Exposed Person (PEP) Iluma Check'
                    }
                </h4>
                <Tooltip title={'Sync data' as string}>
                    <IconButton
                        onClick={async () =>
                            await onRevalidateCft({ id: userId })
                        }
                    >
                        {isValidating ? (
                            <Skeleton variant="rect" width={29} />
                        ) : (
                            <SyncIcon />
                        )}
                    </IconButton>
                </Tooltip>
            </Header>
            {cftInfo.length > 0 ? (
                cftInfo.map((cftData: any, index) => {
                    return (
                        <PaperStyled>
                            <FieldsWrapper>
                                <h4>PPATK Findings {index + 1}</h4>
                                <Field>
                                    <span className="title">{'Score'}:</span>
                                    <span className="value">
                                        {cftData.score || '—'}
                                    </span>
                                </Field>
                                <Field>
                                    <span className="title">
                                        {'CFT & PEP List'}:
                                    </span>
                                    <span className="value">
                                        {cftData.scopes.join(', ') || '—'}
                                    </span>
                                </Field>
                                <Field>
                                    <span className="title">
                                        {'Nationalities'}:
                                    </span>
                                    <span className="value">
                                        {cftData.nationalities
                                            ? cftData.nationalities.join(', ')
                                            : '—'}
                                    </span>
                                </Field>
                            </FieldsWrapper>
                            <FieldsWrapper>
                                <h4>Record data</h4>
                                <Field>
                                    <span className="title">{'PID'}:</span>
                                    <span className="value">
                                        {cftData?.id || '—'}
                                    </span>
                                </Field>
                                <Field>
                                    <span className="title">
                                        {'Name Aliases'}:
                                    </span>
                                    <span className="value">
                                        {cftData?.names
                                            ? cftData?.names.join(', ')
                                            : '—'}
                                    </span>
                                </Field>
                                <Field>
                                    <span className="title">
                                        {'Nationality'}:
                                    </span>
                                    <span className="value">
                                        {cftData?.nationalities.join(', ') ||
                                            '—'}
                                    </span>
                                </Field>
                                <Field>
                                    <span className="title">
                                        {'Date of Birth'}:
                                    </span>
                                    <span className="value">
                                        {cftData?.dates_of_birth
                                            ? cftData.dates_of_birth.map(
                                                  (dobItem: any) => {
                                                      if (
                                                          typeof dobItem ===
                                                          'object'
                                                      ) {
                                                          return `${dobItem.d}/${dobItem.m}/${dobItem.y}`;
                                                      } else {
                                                          return dobItem;
                                                      }
                                                  },
                                              )
                                            : '—'}
                                    </span>
                                </Field>
                            </FieldsWrapper>
                        </PaperStyled>
                    );
                })
            ) : (
                <PaperStyled>
                    <PaperStyled>
                        <FieldsWrapper>
                            <h4>PPATK Findings</h4>
                            <Field>
                                <span className="title">{'Score'}:</span>
                                <span className="value">
                                    {data?.score_threshold || '—'}
                                </span>
                            </Field>
                            <Field>
                                <span className="title">
                                    {'CFT & PEP List'}:
                                </span>
                                <span className="value">
                                    {data?.scopes.join(', ') || '—'}
                                </span>
                            </Field>
                            <Field>
                                <span className="title">
                                    {'Nationalities'}:
                                </span>
                                <span className="value">
                                    {data?.nationality || '—'}
                                </span>
                            </Field>
                        </FieldsWrapper>
                        <FieldsWrapper>
                            <h4>Record data</h4>
                            <Field>
                                <PassedIcon />
                                <h4>
                                    {
                                        'Not included in the CFT/PEP list. Or try to refresh the checking'
                                    }
                                </h4>
                            </Field>
                        </FieldsWrapper>
                    </PaperStyled>
                </PaperStyled>
            )}
            <ButtonsGroup>
                <Button styleType={EButtonType.Outline} onClick={setPrevPage}>
                    <NavigateBeforeIcon /> {'Back'}
                </Button>
                {!disableProcessing && (
                    <>
                        <Button
                            styleType={EButtonType.Danger}
                            disabled={!permissions?.userEKYCEdit}
                            onClick={() => {
                                setRejectDialogOpen(true);
                            }}
                        >
                            {'Reject KYC'}
                        </Button>
                        <Button
                            styleType={EButtonType.Primary}
                            disabled={!permissions?.userEKYCEdit}
                            onClick={() => {
                                setApproveDialogOpen(true);
                            }}
                        >
                            {'Approve KYC'}
                        </Button>
                    </>
                )}
            </ButtonsGroup>

            <RejectDialog
                isDialogOpen={rejectDialogOpen}
                setIsDialogOpen={setRejectDialogOpen}
                onSubmit={async ({ id, reason }) => {
                    await usersEkycSet({
                        id,
                        params: {
                            statusId: EEkycStatuesesApi.EKYC_STATUS_REJECTED,
                            comment: reason,
                        },
                    });
                }}
                userId={userId}
            />
            <ApproveDialog
                isDialogOpen={approveDialogOpen}
                setIsDialogOpen={setApproveDialogOpen}
                title={'Approve KYC Submission'}
                onSubmit={async () => {
                    await usersEkycSet({
                        id: userId,
                        params: {
                            statusId: EEkycStatuesesApi.EKYC_STATUS_VERIFIED,
                            details: updatedData, // pass the updated data to endpoint. updatedData will have the current data or updated info
                        },
                    });
                }}
                kycId={userId}
            />
        </Wrapper>
    );
};

export default CftPepAndApprovalComponents;
